import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import { getUrl } from 'aws-amplify/storage';

interface Tutor {
  id: string;
  name: string;
  photo?: string;
  bio?: string;
  role?: string;
  shortBio?: string;
  expertise?: string;
  experience?: string;
  education?: string;
  company?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  // Reference Projects (up to 5)
  referenceProjectTitle1?: string;
  referenceProjectSummary1?: string;
  referenceProjectLink1?: string;
  referenceProjectTitle2?: string;
  referenceProjectSummary2?: string;
  referenceProjectLink2?: string;
  referenceProjectTitle3?: string;
  referenceProjectSummary3?: string;
  referenceProjectLink3?: string;
  referenceProjectTitle4?: string;
  referenceProjectSummary4?: string;
  referenceProjectLink4?: string;
  referenceProjectTitle5?: string;
  referenceProjectSummary5?: string;
  referenceProjectLink5?: string;
  pressArticleTitle1?: string;
  pressArticleUrl1?: string;
  pressArticleTitle2?: string;
  pressArticleUrl2?: string;
  pressArticleTitle3?: string;
  pressArticleUrl3?: string;
  pressArticleTitle4?: string;
  pressArticleUrl4?: string;
  pressArticleTitle5?: string;
  pressArticleUrl5?: string;
  isActive: boolean;
  isFemale?: boolean;
  courses?: Array<{
    id: string;
    courseId?: string;
    title: string;
    urlSlug?: string;
    isActive: boolean;
  }>;
}

const TutorDetailPage: React.FC = () => {
  const { tutorSlug } = useParams<{ tutorSlug: string }>();
  const navigate = useNavigate();
  const [tutor, setTutor] = useState<Tutor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedPhotoUrl, setResolvedPhotoUrl] = useState<string>('');

  const client = generateClient<Schema>({ authMode: 'iam' });

  useEffect(() => {
    fetchTutorBySlug();
  }, [tutorSlug]);

  const fetchTutorBySlug = async () => {
    if (!tutorSlug) return;
    
    try {
      setLoading(true);
      
      console.log('🔍 Fetching tutors with slug:', tutorSlug);
      
      // Fetch tutors with reference project fields
      console.log('🔍 Fetching tutors with reference project fields...');
      const result = await client.models.Tutor.list({
        selectionSet: [
          'id', 'name', 'photo', 'bio', 'role', 'shortBio', 'expertise', 'experience', 'education',
          'company', 'linkedin', 'scholar', 'twitter', 'website', 'isActive', 'isFemale',
          'referenceProjectTitle1', 'referenceProjectSummary1', 'referenceProjectLink1',
          'referenceProjectTitle2', 'referenceProjectSummary2', 'referenceProjectLink2',
          'referenceProjectTitle3', 'referenceProjectSummary3', 'referenceProjectLink3',
          'referenceProjectTitle4', 'referenceProjectSummary4', 'referenceProjectLink4',
          'referenceProjectTitle5', 'referenceProjectSummary5', 'referenceProjectLink5',
          'pressArticleTitle1', 'pressArticleUrl1',
          'pressArticleTitle2', 'pressArticleUrl2',
          'pressArticleTitle3', 'pressArticleUrl3',
          'pressArticleTitle4', 'pressArticleUrl4',
          'pressArticleTitle5', 'pressArticleUrl5',
          'courses.id', 'courses.courseId', 'courses.title', 'courses.urlSlug', 'courses.isActive'
        ]
      });
      
      const tutors = result.data || [];
      console.log('📝 Tutors loaded:', tutors.length);
      
      // Debug: Log reference project data for first tutor
      if (tutors.length > 0) {
        console.log('🔍 First tutor reference projects:', {
          tutor: tutors[0].name,
          project1: {
            title: tutors[0].referenceProjectTitle1,
            summary: tutors[0].referenceProjectSummary1?.substring(0, 50) + '...',
            link: tutors[0].referenceProjectLink1
          },
          project2: {
            title: tutors[0].referenceProjectTitle2,
            summary: tutors[0].referenceProjectSummary2?.substring(0, 50) + '...',
            link: tutors[0].referenceProjectLink2
          }
        });
      }
      
      // Find tutor by matching slug (using name converted to slug)
      const targetTutor = tutors.find((t: any) => {
        if (!t.name || !t.isActive) return false;
        const nameSlug = createSlug(t.name);
        return nameSlug === tutorSlug;
      });

      if (targetTutor) {
        console.log('✅ Found matching tutor:', targetTutor.name);
        setTutor(targetTutor as Tutor);
        
        // Update page title for SEO
        document.title = `${targetTutor.name} | Dozent | MintKitchen`;
      } else {
        console.log('❌ No matching tutor found for slug:', tutorSlug);
        console.log('Available tutors:', tutors.map((t: any) => ({
          name: t.name,
          slug: t.name ? createSlug(t.name) : 'no-name',
          active: t.isActive
        })));
        setError('Dozent nicht gefunden');
      }
    } catch (err) {
      console.error('🚨 Error fetching tutor:', err);
      setError(`Fehler beim Laden des Dozenten: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Resolve tutor.photo (S3 key or absolute) to usable URL
  useEffect(() => {
    (async () => {
      const path = tutor?.photo || '';
      if (!path) {
        setResolvedPhotoUrl('');
        return;
      }
      try {
        if (path.startsWith('http') || path.startsWith('/')) {
          setResolvedPhotoUrl(path);
          return;
        }
        const publicPath = path.startsWith('public/') ? path : `public/${path}`;
        const { url } = await getUrl({ path: publicPath });
        setResolvedPhotoUrl(url.toString());
      } catch (e) {
        // Fall back to legacy assets if path looked like a simple filename
        if (path.includes('/')) {
          setResolvedPhotoUrl('/assets/images/tutors/default-tutor.svg');
        } else {
          setResolvedPhotoUrl(`/assets/images/tutors/${path}`);
        }
      }
    })();
  }, [tutor?.photo]);

  const createSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9äöüß]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Nicht angegeben';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return `Invalid date: ${dateString}`;
      }
      
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return `Invalid date: ${dateString}`;
    }
  };

  if (loading) {
    return (
      <div className="tutor-detail-loading">
        <div className="spinner"></div>
        <p>Lade Dozenten-Informationen...</p>
      </div>
    );
  }

  if (error || !tutor) {
    return (
      <div className="tutor-detail-error">
        <h2>Dozent nicht gefunden</h2>
        <p>{error || 'Der angeforderte Dozent konnte nicht gefunden werden.'}</p>
        <button onClick={() => navigate('/ki-kurse')} className="back-btn">
          ← Zurück zu den Kursen
        </button>
      </div>
    );
  }

  return (
    <div className="tutor-detail-page">
      {/* Hero Section */}
      <section className="tutor-hero">
        <div className="container">
          <div className="tutor-hero-content">
            <div className="tutor-profile">
              <div className="tutor-image-container">
                {tutor.photo ? (
                  <img 
                    src={resolvedPhotoUrl || '/assets/images/tutors/default-tutor.svg'}
                    alt={`${tutor.name} - Dozent`}
                    className="tutor-hero-image"
                    onError={(e) => {
                      console.warn('⚠️ Tutor photo failed to load:', tutor.photo);
                      e.currentTarget.src = '/assets/images/tutors/default-tutor.svg';
                    }}
                  />
                ) : (
                  <img 
                    src="/assets/images/tutors/default-tutor.svg"
                    alt="Standard Dozent"
                    className="tutor-hero-image"
                  />
                )}
              </div>
              <div className="tutor-hero-info">
                <div className="tutor-label">{(tutor as any)?.isFemale ? 'Dozentin' : 'Dozent'}</div>
                <h1 className="tutor-hero-title">{tutor.name}</h1>
                {/* Removed short bio from hero */}
                {(tutor as any).role && (
                  <div className="tutor-role" style={{ color: '#ffffff', fontWeight: 'normal' }}>
                    {(tutor as any).role}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tutor Content */}
      <section className="tutor-content">
        <div className="container">
          <div className="tutor-content-grid">
            
            {/* Main Content */}
            <div className="tutor-main">
              {tutor.bio && (
                <div className="tutor-section">
                  <h2>Über mich</h2>
                  <p>{tutor.bio}</p>
                </div>
              )}

              {/* Fachgebiete */}
              {tutor.expertise && (
                <div className="tutor-section">
                  <h2>Fachgebiete</h2>
                  <div className="expertise-tags">
                    {tutor.expertise.split(',').map((tag, index) => (
                      <span key={index} className="expertise-tag">
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}




              {/* Reference Projects */}
                    {tutor.referenceProjectTitle1 && [1, 2, 3, 4, 5].some(num =>
        tutor[`referenceProjectTitle${num}` as keyof Tutor]
      ) && (
        <div className="tutor-section">
          <h2>Referenzprojekte</h2>
          <div className="reference-projects">
            {[1, 2, 3, 4, 5].map(num => {
              const title = tutor[`referenceProjectTitle${num}` as keyof Tutor] as string;
              const summary = tutor[`referenceProjectSummary${num}` as keyof Tutor] as string;
              const link = tutor[`referenceProjectLink${num}` as keyof Tutor] as string;
              
              if (!title) return null;
              
              return (
                <div key={num} className="reference-project">
                  <h3 className="project-title">{title}</h3>
                  {summary && <p className="project-summary">{summary}</p>}
                  {link && (
                    <a 
                      href={link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="project-link"
                    >
                      Projekt ansehen →
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}








            </div>

            {/* Sidebar */}
            <div className="tutor-sidebar">

              {/* Qualifikation */}
              {tutor.education && (
                <div className="tutor-contact-card">
                  <h3>Qualifikation</h3>
                  <div className="qualification-items">
                    {tutor.education.split(',').map((item, index) => (
                      <div key={index} className="qualification-item">
                        {item.trim()}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="tutor-contact-card">
                <h3>Links</h3>
                <div className="contact-links">
                  {tutor.linkedin && (
                    <a
                      href={tutor.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link linkedin"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon" aria-hidden="true">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                      LinkedIn
                    </a>
                  )}

                  {tutor.website && (
                    <a
                      href={tutor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link website"
                    >
                      <span>🌐</span> Website
                    </a>
                  )}

                  {(tutor as any).scholar && (
                    <a
                      href={(tutor as any).scholar}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-link scholar"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="icon" aria-hidden="true">
                        <path d="M5.242 13.769L0 9.5 12 0l12 9.5-5.242 4.269C17.548 11.249 14.978 9.5 12 9.5c-2.977 0-5.548 1.748-6.758 4.269zM12 10a7 7 0 1 0 0 14 7 7 0 0 0 0-14z"/>
                      </svg>
                      Google Scholar
                    </a>
                  )}

                </div>
              </div>

              {/* Press Articles */}
              {tutor.pressArticleTitle1 && [1, 2, 3, 4, 5].some(num =>
                tutor[`pressArticleTitle${num}` as keyof Tutor]
              ) && (
                <div className="tutor-contact-card">
                  <h3>Presseartikel</h3>
                  <div className="press-articles-sidebar">
                    {[1, 2, 3, 4, 5].map(num => {
                      const title = tutor[`pressArticleTitle${num}` as keyof Tutor] as string;
                      const url = tutor[`pressArticleUrl${num}` as keyof Tutor] as string;

                      if (!title) return null;

                      return (
                        <a
                          key={num}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="contact-link press-article-link"
                        >
                          <span className="press-article-title">{title}</span>
                          <span className="press-article-arrow">→</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Removed "Meine Kurse" section as requested */}

              {/* Removed link to all courses as requested */}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TutorDetailPage;
